import { findClosestAnchor, getNavigationPolicyForUrl } from "@/main/navigation/NavigationPolicy";
// The eager classifier, never the lazy skeleton module (FI-3157 r158, `bootstrap/eagerGraphCuts.spec.ts`).
import { prefetchNavigationTransitionData } from "@/main/navigation/NavigationTransitionRoutes";
import { prefetchComponentChunk } from "@/main/react/ReactMounter";
import { destinationKeyForUrl, resolveDestinationComponent, resolvePrefetchableData } from "@/main/navigation/NavigationPrefetchTargets";
import { installPrefetchTelemetry, recordPrefetchIssued, resetPrefetchTelemetryForTests } from "@/main/navigation/NavigationPrefetchTelemetry";
import { hasUnresolvedPathSegment } from "@/main/navigation/UnresolvedPathSegment";
import { prefetchFrontendReleaseProbe, prefetchHtmlDocument, resetDocumentPrefetchForTests } from "@/main/navigation/DocumentFetcher";

// Navigation-intent route prefetch: on hover / press-start / focus of a soft-nav-eligible link, warm
// the destination route's JS chunk (and, on press-start only since U44, its critical-path data) so the actual click mounts an
// already-downloaded chunk instead of paying the serial chunk-after-main_bundle round-trip that
// dominates the cold mobile mount. Strictly additive — no history/skeleton/progress. Since U44 (r156) a press may
// start the destination's release probe (HEAD) or HTML document (GET), never with tap-attribution params, and the
// navigation that follows takes that request over (`DocumentFetcher`); hover and focus still fetch no data. See
// designs/01-now/2026-07-09-navigation-intent-route-prefetch-design.md.

let prefetchSetupComplete = false;

// Dedup by path+query so repeated intents toward a destination do real work only once. Bounded so a
// long session with many links can't grow it unboundedly.
const attemptedPrefetchKeys = new Set<string>();
const MAX_TRACKED_KEYS = 256;

// U44 (FI-3676, r156, ui-fundamentals §7c): "anticipate the tap, never the scroll". The chunk (a static asset shared by
// every visit) keeps the whole hover / press / focus ladder; the DATA request (per URL, personalized, the thing that
// costs the server) starts on `pointerdown` only. Hover dwell and keyboard focus are not a tap: a mouse resting on a
// card and a Tab walk through a feed both issued data before this. Data dedup is time-boxed rather than session-long,
// because the data goes stale where the chunk does not: the same window the handover honours (`DocumentFetcher`).
const DATA_PREFETCH_DEDUP_MS = 10_000;
const dataPrefetchIssuedAt = new Map<string, number>();

type PrefetchIntent = "hover" | "press" | "focus";

/** Tap-attribution params (`EnhancedNavigation.TAP_ATTRIBUTION_PARAMS`): a press that becomes a scroll must not record a click. */
const TAP_ATTRIBUTION_PARAMS = ["pl", "ref", "nsrc"];

function carriesTapAttribution(url: URL): boolean {
    return TAP_ATTRIBUTION_PARAMS.some((name) => url.searchParams.has(name));
}

function prefetchDataOnPress(url: URL, key: string): void {
    const target = resolvePrefetchableData(url);
    if (!target) {
        return;
    }
    // The probe and the document request carry the address's own query to the server; the discussion page-data
    // request is keyed by the slug alone, so only the first two can record a click.
    if (target.kind !== "discussion-page-data" && carriesTapAttribution(url)) {
        return;
    }
    const now = Date.now();
    const issuedAt = dataPrefetchIssuedAt.get(key);
    if (issuedAt !== undefined && now - issuedAt <= DATA_PREFETCH_DEDUP_MS) {
        return;
    }
    if (dataPrefetchIssuedAt.size >= MAX_TRACKED_KEYS) {
        dataPrefetchIssuedAt.clear();
    }
    dataPrefetchIssuedAt.set(key, now);

    switch (target.kind) {
        case "discussion-page-data":
            prefetchNavigationTransitionData(url);
            break;
        case "release-probe":
            prefetchFrontendReleaseProbe(target.requestUrl);
            break;
        case "document":
            prefetchHtmlDocument(target.requestUrl);
            break;
    }
    recordPrefetchIssued("data", key);
}

// Hover must *dwell* before we prefetch, so a mouse sweeping across a feed (which fires pointerover on
// every card) never speculatively fetches pages the user isn't actually considering — only a genuine
// pause does. Press-start / keyboard focus are already strong intent and skip the dwell.
const HOVER_DWELL_MS = 80;
let hoverDwellTimer: ReturnType<typeof setTimeout> | null = null;

function cancelHoverDwell(): void {
    if (hoverDwellTimer !== null) {
        clearTimeout(hoverDwellTimer);
        hoverDwellTimer = null;
    }
}

// Respect Data Saver and very slow connections — speculative prefetch is a net negative there.
function isPrefetchDisallowedByConnection(): boolean {
    const connection = (navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    if (!connection) {
        return false;
    }
    return connection.saveData === true
        || connection.effectiveType === "slow-2g"
        || connection.effectiveType === "2g";
}

function prefetchForAnchor(anchor: HTMLAnchorElement, intent: PrefetchIntent): void {
    // Mirror the marker checks in getAnchorNavigationPolicy that don't require a MouseEvent, so a link
    // that would deliberately hard-load is never prefetched.
    if (anchor.dataset.navigation === "hard" || anchor.closest('[data-navigation="hard"]')) {
        return;
    }
    const target = anchor.getAttribute("target");
    if (target && target !== "_self") {
        return;
    }
    if (anchor.hasAttribute("download")) {
        return;
    }
    if ((anchor.getAttribute("rel") ?? "").split(/\s+/).includes("external")) {
        return;
    }
    const href = anchor.getAttribute("href");
    if (!href || href.trim().length === 0) {
        return;
    }

    let url: URL;
    try {
        url = new URL(anchor.href, window.location.href);
    } catch {
        return;
    }

    // Never prefetch the page we're already on.
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
    }

    // An anchor whose href carries a `null`/`undefined` segment is a route built from an id that was
    // not there. This seam is where such an href actually reaches the origin: hover dwell fires a
    // real document fetch, and because no history write is involved, the address-bar guard in
    // `NavigationHistoryState` never runs. That is the mechanism behind the 82 junk requests from 51
    // distinct IPs measured on 2026-08-01 — every referrer was one of our own pages and no user ever
    // saw the URL.
    //
    // Silent `return`, not a logged refusal: a prefetch is speculative, the user has not asked for
    // anything, and the same anchor will log once through `performBrowserNavigation` if it is really
    // clicked. Logging on hover would emit one FRONTEND error per pointer sweep across a feed.
    if (hasUnresolvedPathSegment(url.pathname)) {
        return;
    }

    const key = destinationKeyForUrl(url);
    const chunkAlreadyAttempted = attemptedPrefetchKeys.has(key);
    if (chunkAlreadyAttempted && intent !== "press") {
        return;
    }

    // getNavigationPolicyForUrl already returns "hard" for cross-origin, hash-only, ?lang= locale
    // changes, and ineligible paths — so gating on "soft" filters all of those for free.
    if (getNavigationPolicyForUrl(url).mode !== "soft") {
        return;
    }

    if (!chunkAlreadyAttempted) {
        // Record the attempt even when nothing is prefetchable, so we don't re-evaluate every pointerover.
        if (attemptedPrefetchKeys.size >= MAX_TRACKED_KEYS) {
            attemptedPrefetchKeys.clear();
        }
        attemptedPrefetchKeys.add(key);

        const componentName = resolveDestinationComponent(url);
        // Count only real issuances (prefetchComponentChunk returns false when the chunk is already loaded).
        if (componentName && prefetchComponentChunk(componentName)) {
            recordPrefetchIssued("chunk", componentName);
        }
    }

    // U44: data on a press only, never on hover dwell or focus.
    if (intent === "press") {
        prefetchDataOnPress(url, key);
    }
}

// Hover intent: (re)start the dwell timer for the hovered link. Each pointerover resets it, so a
// sweep-through cancels the previous link's pending prefetch and only a link the pointer rests on for
// HOVER_DWELL_MS actually prefetches. A pointerover off any link cancels a pending dwell.
function handleHoverIntent(event: Event): void {
    if (isPrefetchDisallowedByConnection()) {
        return;
    }
    const anchor = findClosestAnchor(event.target);
    cancelHoverDwell();
    // FI-5800 (design §5b): a touch pointerover (read on the event itself) never arms the dwell; pen keeps it (⚑AT1).
    if (!anchor || (event as Partial<PointerEvent>).pointerType === "touch") {
        return;
    }
    hoverDwellTimer = setTimeout(() => {
        hoverDwellTimer = null;
        prefetchForAnchor(anchor, "hover");
    }, HOVER_DWELL_MS);
}

// FI-5796 (r192j, design §5b): a TOUCH press waits until the pointer sequence is a tap. A fling that starts on a link
// row used to import the destination's cold chunk closure mid-fling (r192i: 86 chunks, 0/5 warm vs 5/5 cold over the
// 6x frame ceiling). Decided from the sequence (`pointerup` = tap, `pointercancel` / `scroll` = pan), never from a field
// on a later click (`AGENTS/frontend-runtime.md`, two-event contract). Held past the timer with neither, it fires anyway.
// Renders nothing and changes no state the tree reads (rule 26 warm-up clause).
const TOUCH_PRESS_SETTLE_MS = HOVER_DWELL_MS;
let pendingTouchPress: { anchor: HTMLAnchorElement; timer: ReturnType<typeof setTimeout> } | null = null;

function cancelPendingTouchPress(): void {
    if (pendingTouchPress !== null) {
        clearTimeout(pendingTouchPress.timer);
        pendingTouchPress = null;
    }
}

function firePendingTouchPress(): void {
    if (pendingTouchPress === null) {
        return;
    }
    const { anchor } = pendingTouchPress;
    cancelPendingTouchPress();
    prefetchForAnchor(anchor, "press");
}

function armPendingTouchPress(anchor: HTMLAnchorElement): void {
    cancelPendingTouchPress();
    pendingTouchPress = {
        anchor,
        timer: setTimeout(firePendingTouchPress, TOUCH_PRESS_SETTLE_MS),
    };
}

function isTouchPointerDown(event: Event): boolean {
    // Read on the `pointerdown` itself, the sequence start; an event with no `pointerType` keeps the immediate path.
    return event.type === "pointerdown" && (event as Partial<PointerEvent>).pointerType === "touch";
}

// Strong intent (press-start / keyboard focus) is an imminent navigation, so prefetch immediately with
// no dwell. Only the press also starts data (U44). A touch press is deferred until it is a tap (above).
function handleStrongIntent(event: Event): void {
    if (isPrefetchDisallowedByConnection()) {
        return;
    }
    const anchor = findClosestAnchor(event.target);
    if (!anchor) {
        if (event.type === "pointerdown") {
            cancelPendingTouchPress();
        }
        return;
    }
    cancelHoverDwell();
    if (isTouchPointerDown(event)) {
        armPendingTouchPress(anchor);
        return;
    }
    prefetchForAnchor(anchor, event.type === "pointerdown" ? "press" : "focus");
}

function handlePressEnd(): void {
    firePendingTouchPress();
}

function handlePressAbandoned(): void {
    cancelPendingTouchPress();
}

export function setupNavigationPrefetch(): void {
    if (prefetchSetupComplete || typeof document === "undefined") {
        return;
    }
    prefetchSetupComplete = true;

    const options: AddEventListenerOptions = { passive: true, capture: false };
    // pointerover (not pointerenter) bubbles, so one delegated listener covers all links.
    document.addEventListener("pointerover", handleHoverIntent, options);
    document.addEventListener("pointerdown", handleStrongIntent, options);
    document.addEventListener("focusin", handleStrongIntent, options);
    // FI-5796: the rest of a touch press's sequence. `scroll` does not bubble, so it is captured to see nested scrollers.
    document.addEventListener("pointerup", handlePressEnd, options);
    document.addEventListener("pointercancel", handlePressAbandoned, options);
    document.addEventListener("scroll", handlePressAbandoned, { passive: true, capture: true });

    installPrefetchTelemetry();
}

export function resetNavigationPrefetchForTests(): void {
    if (typeof document !== "undefined") {
        document.removeEventListener("pointerover", handleHoverIntent);
        document.removeEventListener("pointerdown", handleStrongIntent);
        document.removeEventListener("focusin", handleStrongIntent);
        document.removeEventListener("pointerup", handlePressEnd);
        document.removeEventListener("pointercancel", handlePressAbandoned);
        document.removeEventListener("scroll", handlePressAbandoned, { capture: true });
    }
    cancelHoverDwell();
    cancelPendingTouchPress();
    resetPrefetchTelemetryForTests();
    resetDocumentPrefetchForTests();
    prefetchSetupComplete = false;
    attemptedPrefetchKeys.clear();
    dataPrefetchIssuedAt.clear();
}
